import { Component } from '@theme/component';
import { ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';

const PRODUCT_CARD_STITCHING_SELECT = 'product-card:stitching-select';

/** Section that renders one product's drawer body (sections/quick-add-drawer-content.liquid). */
const DRAWER_CONTENT_SECTION_ID = 'quick-add-drawer-content';

/**
 * Drawer bodies fetched so far, by request URL. Each product's body is fetched at most once per
 * page view, shared by every card showing that product (or one of its colours) and by the in-drawer
 * colour swatches. A failed request is dropped so the next attempt retries.
 * @type {Map<string, Promise<HTMLElement>>}
 */
const drawerContentRequests = new Map();

/**
 * @param {string} productUrl - Any URL of the product (query and hash are ignored).
 * @returns {string}
 */
function drawerContentUrl(productUrl) {
  const url = new URL(productUrl, window.location.origin);
  url.search = '';
  url.hash = '';
  url.searchParams.set('section_id', DRAWER_CONTENT_SECTION_ID);
  return url.toString();
}

/**
 * Fetches (or reuses) a product's quick add drawer body.
 *
 * The drawer used to be filled from `<template>`s embedded in every product card for the card's
 * product and each stitched colour sibling - several MB of HTML on a collection page that was
 * almost never opened. Fetching on demand keeps pages and Nosto card responses small; hover /
 * touch on the trigger starts the request early so the drawer usually opens with content ready.
 *
 * @param {string} productUrl
 * @returns {Promise<HTMLElement>} A fresh copy of the drawer body, ready to insert.
 */
export function loadQuickAddDrawerContent(productUrl) {
  const requestUrl = drawerContentUrl(productUrl);

  let request = drawerContentRequests.get(requestUrl);
  if (!request) {
    request = fetch(requestUrl)
      .then(async (response) => {
        if (!response.ok) throw new Error(`Quick add drawer request failed (${response.status})`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const panel = html.querySelector('[data-quick-add-drawer-content]');
        if (!(panel instanceof HTMLElement)) throw new Error('Quick add drawer content missing');
        return panel;
      })
      .catch((error) => {
        drawerContentRequests.delete(requestUrl);
        throw error;
      });
    drawerContentRequests.set(requestUrl, request);
  }

  return request.then((panel) => /** @type {HTMLElement} */ (document.importNode(panel, true)));
}

/**
 * Scripts that arrive in fetched (DOMParser) markup don't run when inserted; re-create them so they
 * do. Module scripts are only evaluated once per URL, so this is a no-op if already loaded.
 * @param {Element} root
 */
function activateScripts(root) {
  for (const script of root.querySelectorAll('script[src]')) {
    const replacement = document.createElement('script');
    for (const { name, value } of script.attributes) replacement.setAttribute(name, value);
    script.replaceWith(replacement);
  }
}

/**
 * Placeholder shown in the drawer while a product's body is loading.
 * @returns {HTMLElement}
 */
function createDrawerLoadingPanel() {
  const panel = document.createElement('div');
  panel.className = 'quick-add-drawer-content quick-add-drawer-content--loading';
  panel.setAttribute('aria-busy', 'true');
  panel.innerHTML = '<span class="quick-add-drawer__loader" aria-hidden="true"></span>';
  return panel;
}

export class QuickAddComponent extends Component {
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  /**
   * Product id from card stitching, or this card's default product.
   * @returns {string | null}
   */
  #getActiveProductId() {
    const productCard = this.closest('product-card');
    if (productCard instanceof HTMLElement && productCard.dataset.productId) {
      return productCard.dataset.productId;
    }

    return this.dataset.productId || null;
  }

  /**
   * URL of the product the card currently shows - card stitching points the card's links at the
   * selected colour's product, so the card link is used when present.
   * @returns {string | null}
   */
  #getActiveProductUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const cardLink = productCard?.getProductCardLink?.();
    if (cardLink?.href) return cardLink.href;

    const resourceLink = this.closest('.resource-card')?.querySelector('a[href*="/products/"]');
    if (resourceLink instanceof HTMLAnchorElement) return resourceLink.href;

    return this.dataset.productUrl || null;
  }

  /** Starts loading the drawer body as soon as the shopper shows intent (hover / touch / focus). */
  #prefetchDrawerContent = () => {
    const productUrl = this.#getActiveProductUrl();
    if (!productUrl) return;
    loadQuickAddDrawerContent(productUrl).catch(() => {
      // Retried (and reported) when the drawer is actually opened.
    });
  };

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));

    this.addEventListener('pointerenter', this.#prefetchDrawerContent);
    this.addEventListener('pointerdown', this.#prefetchDrawerContent);
    this.addEventListener('focusin', this.#prefetchDrawerContent);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));

    this.removeEventListener('pointerenter', this.#prefetchDrawerContent);
    this.removeEventListener('pointerdown', this.#prefetchDrawerContent);
    this.removeEventListener('focusin', this.#prefetchDrawerContent);
  }

  #handleCartUpdate = () => {};

  /** Increments with every drawer load, so a slower earlier response can't replace a newer one. */
  #drawerLoadId = 0;

  /**
   * Opens the global drawer immediately and fills it with this card's product.
   * @param {Event} event
   */
  handleClick = (event) => {
    event.preventDefault();

    const dialogComponent = document.getElementById('quick-add-dialog');
    if (dialogComponent && typeof dialogComponent.setSourceComponent === 'function') {
      dialogComponent.setSourceComponent(this);
    }

    const productUrl = this.#getActiveProductUrl();
    if (!productUrl) {
      console.warn('Quick add: no product URL found for this card');
      return;
    }

    this.#loadDrawerProduct(this.#getActiveProductId(), productUrl, false);
    this.#openQuickAddModal();
  };

  /**
   * Swap drawer to another stitched sibling product.
   * @param {string} productId
   * @param {string} [productUrl]
   * @returns {boolean} Whether the swap started.
   */
  loadDrawerProduct(productId, productUrl) {
    if (!productUrl) return false;
    this.#loadDrawerProduct(productId, productUrl, true);
    return true;
  }

  /**
   * Shows the product's drawer body - straight away when it's already loaded, otherwise after a
   * loading state (the drawer never shows a previous product's content in the meantime).
   * @param {string | null | undefined} productId
   * @param {string} productUrl
   * @param {boolean} switchingColour - A colour picked inside the open drawer: keep its current
   *   layout (marked busy) while loading, instead of the placeholder used when opening the drawer.
   */
  #loadDrawerProduct(productId, productUrl, switchingColour) {
    const loadId = ++this.#drawerLoadId;

    const productCard = this.closest('product-card');
    if (productCard instanceof HTMLElement && productId) {
      productCard.dataset.productId = productId;
    }

    let settled = false;
    loadQuickAddDrawerContent(productUrl)
      .then((panel) => {
        settled = true;
        if (loadId !== this.#drawerLoadId || !this.#isDrawerSource()) return;
        this.updateQuickAddModal(panel);
        activateScripts(panel);
      })
      .catch((error) => {
        settled = true;
        if (loadId !== this.#drawerLoadId || !this.#isDrawerSource()) return;
        console.error(error);
        this.#showDrawerError(productUrl);
      });

    // Already loaded: the promise settles in a microtask, before the next frame - no placeholder flash.
    queueMicrotask(() => {
      queueMicrotask(() => {
        if (settled || loadId !== this.#drawerLoadId) return;
        const modalContent = document.getElementById('quick-add-modal-content');
        const current = modalContent?.querySelector('[data-quick-add-drawer-content]');
        if (switchingColour && current instanceof HTMLElement) {
          current.setAttribute('aria-busy', 'true');
          return;
        }
        modalContent?.replaceChildren(createDrawerLoadingPanel());
      });
    });
  }

  /** @returns {boolean} Whether the quick add drawer currently belongs to this card. */
  #isDrawerSource() {
    const dialogComponent = /** @type {any} */ (document.getElementById('quick-add-dialog'));
    return !dialogComponent || !dialogComponent.sourceQuickAdd || dialogComponent.sourceQuickAdd === this;
  }

  /**
   * @param {string} productUrl
   */
  #showDrawerError(productUrl) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    const panel = document.createElement('div');
    panel.className = 'quick-add-drawer-content quick-add-drawer-content--error';
    panel.setAttribute('role', 'alert');

    const message = document.createElement('p');
    message.textContent = Theme.translations?.add_to_cart_error || 'Something went wrong. Please try again.';
    const link = document.createElement('a');
    link.className = 'button';
    link.href = productUrl;
    link.textContent = this.dataset.productTitle || 'View product';

    panel.append(message, link);
    modalContent.replaceChildren(panel);
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!dialogComponent) return;

    dialogComponent.querySelector('.quick-add-drawer__content')?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!dialogComponent || typeof dialogComponent.showDialog !== 'function') return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);
    dialogComponent.showDialog();

    const dialog = dialogComponent.refs?.dialog || dialogComponent.querySelector('dialog');
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (dialogComponent && typeof dialogComponent.closeDialog === 'function') {
      dialogComponent.closeDialog();
    }
  };

  /**
   * No-op — drawer content is loaded by `loadQuickAddDrawerContent`.
   * @returns {Promise<null>}
   */
  async fetchProductPage() {
    return null;
  }

  /**
   * @param {Element} productGrid
   */
  updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    modalContent.replaceChildren(productGrid);
    this.#syncDrawerVariantSelection(modalContent);
  }

  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    this.setAttribute('data-quick-add-button', 'choose');
  }

  /**
   * @param {Element} modalContent
   */
  #syncDrawerVariantSelection(modalContent) {
    const checkedInput = modalContent.querySelector(
      'input[type="radio"][data-variant-id]:checked, input[type="radio"][data-variant-id][data-current-checked="true"]'
    );

    if (checkedInput instanceof HTMLInputElement) {
      checkedInput.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const firstAvailable = modalContent.querySelector(
      'input[type="radio"][data-variant-id][data-option-available="true"]'
    );

    if (firstAvailable instanceof HTMLInputElement) {
      firstAvailable.checked = true;
      firstAvailable.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  /** @type {QuickAddComponent | null} */
  #sourceQuickAdd = null;

  #handleWishlistClick = (event) => {
    if (event.target instanceof Element) {
      const button = event.target.closest('wishlist-button-pdp');
      if (button && button.getAttribute('data-is-added') === 'false') {
        setTimeout(() => {
          this.closeDialog();
        }, 150);
      }
    }
  };

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.addEventListener('click', this.#handleDrawerClick, true);
    this.addEventListener('click', this.#handleWishlistClick, true);
    this.addEventListener('pointerover', this.#prefetchSwatchProduct);
    this.addEventListener('pointerdown', this.#prefetchSwatchProduct, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.removeEventListener('click', this.#handleDrawerClick, true);
    this.removeEventListener('click', this.#handleWishlistClick, true);
    this.removeEventListener('pointerover', this.#prefetchSwatchProduct);
    this.removeEventListener('pointerdown', this.#prefetchSwatchProduct, true);
  }

  /**
   * Starts loading a colour's drawer body when its swatch is hovered or touched.
   * @param {Event} event
   */
  #prefetchSwatchProduct = (event) => {
    const button = event.target instanceof Element ? event.target.closest('.quick-add-drawer__swatch-btn') : null;
    if (!(button instanceof HTMLButtonElement) || !button.dataset.productUrl) return;
    if (button.classList.contains('product-card-stitching__swatch--active')) return;
    loadQuickAddDrawerContent(button.dataset.productUrl).catch(() => {});
  };

  /**
   * @param {QuickAddComponent} component
   */
  setSourceComponent(component) {
    this.#sourceQuickAdd = component;
    this.sourceQuickAdd = component;
  }

  /**
   * @param {Event} event
   */
  #handleDrawerClick = (event) => {
    const button =
      event.target instanceof Element ? event.target.closest('.quick-add-drawer__swatch-btn') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent?.contains(button)) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (button.classList.contains('product-card-stitching__swatch--active')) return;

    const productId = button.dataset.productId;
    if (!productId || !this.#sourceQuickAdd) return;

    // Selected state responds immediately; the colour's body replaces the drawer once loaded.
    button.closest('.quick-add-drawer__swatch-list')?.querySelectorAll('.quick-add-drawer__swatch-btn').forEach((swatch) => {
      const isActive = swatch === button;
      swatch.classList.toggle('product-card-stitching__swatch--active', isActive);
      if (isActive) swatch.setAttribute('aria-current', 'true');
      else swatch.removeAttribute('aria-current');
    });

    if (!this.#sourceQuickAdd.loadDrawerProduct(productId, button.dataset.productUrl)) return;

    const productCard = this.#sourceQuickAdd.closest('product-card');
    if (productCard instanceof HTMLElement) {
      productCard.dispatchEvent(
        new CustomEvent(PRODUCT_CARD_STITCHING_SELECT, {
          bubbles: false,
          detail: { productId },
        })
      );
    }

    this.querySelector('.quick-add-drawer__content')?.scrollTo({ top: 0, behavior: 'instant' });
  };

  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #handleDialogClose = () => {
    this.#sourceQuickAdd = null;

    const iosVersion = getIOSVersion();
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
